/**
 * Collect the `app_users` ids a task is assigned to.
 *
 * `assigned_users` is a `text[]` of ids and the task hooks normalize it to that
 * shape (`normalizeTaskAssignedUsers`), but this reads a
 * `Record<string, unknown>` because it also folds in the legacy `assigned_to`
 * scalar and the `assigned_user` JOIN, neither of which is a column on
 * `contact_tasks` and so neither is on the codegen row type.
 *
 * The object branch below is defensive only — a row cached before the
 * id→object enrichment swap was deleted (#2634), or a form control that emits
 * `{ id }`. There is deliberately no companion that reads NAMES off such an
 * object: assignee identity is resolved by id from the user directory
 * (`useAdminUsers()`, plus `TaskAssigneesDisplay`'s own `app_users` lookup for
 * anyone the directory does not carry), which is both authoritative and
 * current.
 */
export function collectAssigneeIdsFromTask(item: Record<string, unknown>): string[] {
  const ids: string[] = [];
  const raw = item.assigned_users;
  if (Array.isArray(raw)) {
    for (const u of raw) {
      if (typeof u === "string" && u.trim()) {
        ids.push(u);
      } else if (
        u &&
        typeof u === "object" &&
        "id" in u &&
        typeof (u as { id: string }).id === "string"
      ) {
        ids.push((u as { id: string }).id);
      }
    }
  }
  const assignedTo = item.assigned_to;
  if (typeof assignedTo === "string" && assignedTo.trim()) ids.push(assignedTo);
  const au = item.assigned_user as { id?: string } | undefined;
  if (au?.id) ids.push(au.id);
  return [...new Set(ids)];
}
